import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Paper,
  Alert,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../util';
import { stepperStyles } from '../shared/styles';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  // Populate email from URL query parameter on component mount
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const res = await resetPassword(email);
      if (!res.success) {
        setError(res.exception);
      } else {
        setIsSubmitted(true);
      }
    } catch (err) {
      setError('Failed to send reset password email. Please try again.');
      console.error('Error during password reset:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ ...stepperStyles.pageContainer, mt: 4, p: 1 }}>
      <Box sx={stepperStyles.centeredContent}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={stepperStyles.pageTitle(theme)}
        >
          Reset Password
        </Typography>
        <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
          Enter your email address to receive a password reset link
        </Typography>

        {isSubmitted && !error ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              border: `1px solid ${theme.palette.success.light}`,
              backgroundColor: `${theme.palette.success.main}08`,
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 2,
                }}
              >
                <CheckCircle
                  sx={{
                    color: theme.palette.success.main,
                    fontSize: '2rem',
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.success.dark,
                    fontWeight: 'bold',
                  }}
                >
                  Reset Link Sent
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.success.dark,
                lineHeight: 1.6,
                textAlign: 'center',
              }}
            >
              A password reset link has been sent to your email address. Please
              check your inbox and follow the instructions to reset your
              password.
            </Typography>
          </Paper>
        ) : (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  {error}
                </Alert>
              )}
              <TextField
                size="small"
                id="email"
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                variant="outlined"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!email.trim() || isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Navigation Links */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mt: 4,
            justifyContent: 'center',
          }}
        >
          <Link to="/notification" style={{ textDecoration: 'none' }}>
            <Typography
              variant="button"
              sx={{
                color: theme.palette.text.primary,
                fontSize: '0.875rem',
                fontWeight: 'medium',
                px: 2,
                py: 1,
                borderRadius: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Back to Login
            </Typography>
          </Link>
          <Link to="/newaccount" style={{ textDecoration: 'none' }}>
            <Typography
              variant="button"
              sx={{
                color: theme.palette.text.primary,
                fontSize: '0.875rem',
                fontWeight: 'medium',
                px: 2,
                py: 1,
                borderRadius: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Create New Account
            </Typography>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
